import { API, BACKEND_URL } from '@/config/api';
import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { isDemoUser } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { MessageCircle, Bot, User, X, Loader2, Send } from 'lucide-react';

export default function AIChatWidget() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  
  const loadChatHistory = useCallback(async () => {
    // For demo users, show example chat
    if (isDemoUser(user)) {
      setMessages([
        {
          id: '1',
          message: 'Hva kan du fortelle meg om algene i mine anlegg?',
          response: 'Basert på dine siste prøver ser jeg at du har hatt normale algenivåer. Alle verdier er innenfor grønne grenseverdier. Jeg anbefaler å følge med på utviklingen.',
          timestamp: new Date().toISOString()
        }
      ]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/chat/history`, { withCredentials: true });
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  }, [user]);
  
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadChatHistory();
    }
  }, [isOpen, messages.length, loadChatHistory]);
  
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);
  
  const sendMessage = async () => {
    if (!currentMessage.trim() || isLoading) return;
    
    const messageText = currentMessage.trim();
    setCurrentMessage('');
    setIsLoading(true);
    
    // For demo users, show example responses
    if (isDemoUser(user)) {
      const newUserMessage = {
        id: Date.now().toString(),
        message: messageText,
        response: '',
        timestamp: new Date().toISOString(),
        isUser: true
      };
      setMessages(prev => [...prev, newUserMessage]);
      
      setTimeout(() => {
        const mockResponses = {
          'trend': 'Trendene viser en økning i kiselalger i Rogaland-området. Skeletonema har økt med 15% siste måned.',
          'risiko': 'Basert på dine data ser jeg at alle områder har grønne verdier. Ingen forhøyede algenivåer registrert.',
          'alge': 'Algene i dine områder viser normale verdier. Alle målinger er under gule grenseverdier.',
          'varme': 'Vanntemperaturen varierer fra 7.8°C til 8.5°C, som er optimalt for vinterperioden.',
          'anbefaling': 'Jeg anbefaler å fortsette rutinemessig overvåking. Alle verdier er innenfor normale grenser.'
        };
        
        let response = 'Takk for spørsmålet! ';
        const lowerMessage = messageText.toLowerCase();
        
        if (lowerMessage.includes('trend')) response = mockResponses.trend;
        else if (lowerMessage.includes('risiko')) response = mockResponses.risiko;
        else if (lowerMessage.includes('alge')) response = mockResponses.alge;
        else if (lowerMessage.includes('varme') || lowerMessage.includes('grader') || lowerMessage.includes('celsius')) response = mockResponses.varme;
        else if (lowerMessage.includes('anbefal')) response = mockResponses.anbefaling;
        else response = 'Basert på dine data kan jeg hjelpe deg med å forstå algetrender, fargekoding (grønn/gul/oransje/rød) og gi anbefalinger. Spør meg om trender, risiko, eller spesifikke alger.';
        
        setMessages(prev => [...prev.slice(0, -1), { ...newUserMessage, response: response }]);
        setIsLoading(false);
      }, 1500);
      
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/chat`, 
        { message: messageText },
        { withCredentials: true }
      );
      
      const newMessage = {
        id: Date.now().toString(),
        message: messageText,
        response: response.data.response,
        timestamp: response.data.timestamp
      };
      
      setMessages(prev => [...prev, newMessage]);
      setIsLoading(false);
      
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Kunne ikke sende melding');
      setIsLoading(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          data-testid="open-chat-btn"
          className="w-16 h-16 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 shadow-xl hover:shadow-2xl transition-all"
        >
          <MessageCircle className="w-8 h-8 text-white" />
        </Button>
      </div>
    );
  }
  
  return (
    <div className="fixed bottom-6 right-6 z-50">
      <Card className="w-96 h-[600px] border-0 shadow-2xl bg-white">
        <CardHeader className="pb-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center space-x-2">
              <Bot className="w-5 h-5" />
              <span>Algae AI Assistent</span>
            </CardTitle>
            <Button
              onClick={() => setIsOpen(false)}
              data-testid="close-chat-btn"
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-teal-100">Spor om algetrends og analyser</p>
        </CardHeader>
        
        <CardContent className="p-0 h-[480px] flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && !isLoading && (
              <div className="text-center text-slate-500 mt-8">
                <Bot className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">Hei! Jeg kan hjelpe deg med å forstå algedata og trender.</p>
                <p className="text-xs mt-2">Spor meg om risiko, trends eller anbefalinger.</p>
              </div>
            )}
            
            {messages.map((msg, index) => (
              <div key={msg.id || index} className="space-y-3">
                <div className="flex justify-end">
                  <div className="max-w-[80%] bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-2xl rounded-br-md px-4 py-3">
                    <div className="flex items-start space-x-2">
                      <User className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{msg.message}</span>
                    </div>
                  </div>
                </div>
                
                {msg.response && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%] bg-slate-100 text-slate-900 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-start space-x-2">
                        <Bot className="w-4 h-4 mt-0.5 flex-shrink-0 text-teal-600" />
                        <span className="text-sm whitespace-pre-wrap">{msg.response}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="max-w-[80%] bg-slate-100 text-slate-900 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                    <span className="text-sm text-slate-600">Analyserer data...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <div className="flex space-x-2">
              <Input
                data-testid="chat-input"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Spor om algetrends, risiko eller anbefalinger..."
                disabled={isLoading}
                className="flex-1 border-slate-300 focus:border-teal-500"
              />
              <Button
                onClick={sendMessage}
                data-testid="send-message-btn"
                disabled={isLoading || !currentMessage.trim()}
                className="bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 text-white"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}